import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
  User,
  sendPasswordResetEmail
} from "firebase/auth";
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  setDoc,
  onSnapshot
} from "firebase/firestore";

import { auth, db } from "./config";

// Define types
export interface UserProfile {
  id: string;
  email?: string;
  displayName?: string;
  roleId?: number;
  customerId?: string; // ID du customer associé
  createdAt?: any;
  updatedAt?: any;
  [key: string]: any;
}

export interface UserRole {
  roleId: number;
  createdAt?: any;
  updatedAt?: any;
}

export interface Address {
  id: string;
  street: string;
  city: string;
  postalCode: string;
  country: string;
  isMain: boolean;
  createdAt?: any;
  updatedAt?: any;
}

export interface Customer {
  id: string;
  customerId: string; // ID unique généré pour le client
  name: string;
  companyName: string;
  logoUrl?: string; // URL du logo de l'entreprise
  addresses?: Address[]; // Changé de address (string) à addresses (tableau d'objets Address)
  rating?: number | null; // Évaluation du client (0-5)
  notes?: string; // Notes concernant le client
  phone?: string;
  email?: string;
  website?: string;
  description?: string;
  status: 'active' | 'inactive';
  industry?: string;
  companySize?: string;
  userIds?: string[]; // Liste des IDs des utilisateurs associés à ce client
  createdAt: any;
  updatedAt: any;
}

// Auth services
export const registerUser = async (email: string, password: string, displayName: string, customerId?: string) => {
  try {
    // Step 1: Create the Firebase auth user
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(userCredential.user, { displayName });
    
    try {
      // Create user document in users collection with default role (2 = client)
      await setDoc(doc(db, "users", userCredential.user.uid), {
        email: email,
        displayName: displayName,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        roleId: 2, // Default role (client)
        customerId: customerId || null // Link to customer if provided
      });
    } catch (firestoreError) {
      console.error("Error creating user document:", firestoreError);
      // Even if Firestore operations fail, we still return the user
      // so they can at least use basic auth functionality
    }
    
    return userCredential.user;
  } catch (error) {
    console.error("Registration error:", error);
    throw error;
  }
};

export const loginUser = async (email: string, password: string) => {
  const userCredential = await signInWithEmailAndPassword(auth, email, password);
  return userCredential.user;
};

export const logoutUser = async () => {
  await signOut(auth);
};

// User roles services
export const getUserRole = async (userId: string): Promise<UserRole | null> => {
  try {
    const userRef = doc(db, "users", userId);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists() && userSnap.data().roleId) {
      return { roleId: userSnap.data().roleId };
    }
    return null;
  } catch (error) {
    console.error("Error getting user role:", error);
    // Return default client role if there's an error
    return { roleId: 2 };
  }
};

export const updateUserRole = async (userId: string, roleId: number) => {
  try {
    const userRef = doc(db, "users", userId);
    await updateDoc(userRef, {
      roleId,
      updatedAt: Timestamp.now()
    });
    return true;
  } catch (error) {
    console.error("Error updating user role:", error);
    return false;
  }
};

// Firestore services
export const createTicket = async (ticketData: any) => {
  const ticketsCollection = collection(db, "tickets");
  
  // Génère un ID unique pour le ticket (format: TICK-YYYYMMDD-XXXX)
  const date = new Date();
  const dateStr = date.getFullYear().toString() +
                 (date.getMonth() + 1).toString().padStart(2, '0') +
                 date.getDate().toString().padStart(2, '0');
  const randomPart = Math.floor(1000 + Math.random() * 9000); // 4 chiffres aléatoires
  const ticketId = `TICK-${dateStr}-${randomPart}`;
  
  return await addDoc(ticketsCollection, {
    ...ticketData,
    ticketId, // Ajoute l'ID unique généré
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
    status: ticketData.status || "new"
  });
};

export const updateTicket = async (ticketId: string, ticketData: any) => {
  const ticketRef = doc(db, "tickets", ticketId);
  return await updateDoc(ticketRef, {
    ...ticketData,
    updatedAt: Timestamp.now()
  });
};

export const getTicket = async (ticketId: string) => {
  try {
    const ticketRef = doc(db, "tickets", ticketId);
    const ticketSnap = await getDoc(ticketRef);
    
    if (!ticketSnap.exists()) {
      return null;
    }
    
    const ticketData = { id: ticketSnap.id, ...ticketSnap.data() } as any;
    
    // Si le ticket a un userId, récupérer les informations de l'utilisateur
    if (ticketData.userId) {
      try {
        const userProfile = await getUserProfile(ticketData.userId);
        if (userProfile) {
          // Enrichir le ticket avec les informations de l'utilisateur
          ticketData.userName = userProfile.displayName || ticketData.userName;
          ticketData.userEmail = userProfile.email || ticketData.userEmail;
          
          // Si le ticket n'a pas de customerId mais que l'utilisateur est associé à un client
          if (!ticketData.customerId && userProfile.customerId) {
            const customerData = await getCustomer(userProfile.customerId);
            if (customerData) {
              // Enrichir le ticket avec les informations du client associé à l'utilisateur
              ticketData.customerId = userProfile.customerId;
              ticketData.customerName = customerData.companyName || customerData.name;
            }
          }
        }
      } catch (error) {
        console.error(`Error fetching user details for ticket ${ticketData.id}:`, error);
      }
    }
    
    // Si le ticket a un customerId, récupérer les informations du client
    if (ticketData.customerId) {
      try {
        const customerData = await getCustomer(ticketData.customerId);
        if (customerData) {
          // Enrichir le ticket avec les informations du client
          ticketData.customerName = customerData.companyName || customerData.name;
        }
      } catch (error) {
        console.error(`Error fetching customer details for ticket ${ticketData.id}:`, error);
      }
    }
    
    return ticketData;
  } catch (error) {
    console.error(`Error getting ticket ${ticketId}:`, error);
    return null;
  }
};

export const getTickets = async (filters = {}) => {
  const ticketsCollection = collection(db, "tickets");
  let ticketsQuery = query(ticketsCollection, orderBy("createdAt", "desc"));
  
  // Apply filters if any
  Object.entries(filters).forEach(([key, value]) => {
    if (value) {
      ticketsQuery = query(ticketsQuery, where(key, "==", value));
    }
  });
  
  const querySnapshot = await getDocs(ticketsQuery);
  const tickets: any[] = [];
  
  querySnapshot.forEach((doc) => {
    tickets.push({
      id: doc.id,
      ...doc.data()
    });
  });
  
  return tickets;
};

// User profile service
export const getUserProfile = async (userId: string): Promise<UserProfile | null> => {
  try {
    const userRef = doc(db, "users", userId);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      return { id: userSnap.id, ...userSnap.data() } as UserProfile;
    } else {
      return null;
    }
  } catch (error) {
    console.error("Error getting user profile:", error);
    return null;
  }
};

export const updateUserProfile = async (userId: string, profileData: any): Promise<boolean> => {
  try {
    const userRef = doc(db, "users", userId);
    
    // Ensure we're not overwriting any fields with undefined
    const cleanedData: Record<string, any> = {};
    
    // Only include fields that are present in profileData
    if (profileData.displayName !== undefined) cleanedData.displayName = profileData.displayName;
    if (profileData.email !== undefined) cleanedData.email = profileData.email;
    if (profileData.roleId !== undefined) cleanedData.roleId = profileData.roleId;
    if (profileData.jobTitle !== undefined) cleanedData.jobTitle = profileData.jobTitle;
    if (profileData.avatarUrl !== undefined) cleanedData.avatarUrl = profileData.avatarUrl;
    
    // Always update the updatedAt timestamp
    cleanedData.updatedAt = Timestamp.now();
    
    await updateDoc(userRef, cleanedData);
    return true;
  } catch (error) {
    console.error("Error updating user profile:", error);
    return false;
  }
};

// --- Live Chat Services ---

/**
 * Envoie un message dans une session de tchat en direct.
 * @param sessionId ID de la session de tchat (par exemple userId ou ticketId)
 * @param message Objet message contenant le texte et les informations utilisateur
 */
export const sendLiveChatMessage = async (sessionId: string, message: { 
  text: string; 
  userId: string; 
  displayName: string;
  isStaff: boolean;
  avatar?: string;
}) => {
  const messagesCollection = collection(db, "liveChats", sessionId, "messages");
  return await addDoc(messagesCollection, {
    ...message,
    createdAt: Timestamp.now(),
  });
};

/**
 * S'abonne en temps réel aux messages d'une session de tchat.
 * @param sessionId ID de la session de tchat
 * @param callback Fonction appelée à chaque mise à jour des messages
 * @returns unsubscribe function
 */
export const subscribeToLiveChatMessages = (sessionId: string, callback: (messages: any[]) => void) => {
  const messagesCollection = collection(db, "liveChats", sessionId, "messages");
  const q = query(messagesCollection, orderBy("createdAt", "asc"));
  return onSnapshot(q, (snapshot) => {
    const messages: any[] = [];
    snapshot.forEach((doc) => {
      messages.push({ id: doc.id, ...doc.data() });
    });
    callback(messages);
  });
};

/**
 * Crée ou met à jour une session de tchat
 * @param sessionData Données de la session
 * @returns Promise avec la référence du document
 */
export const createOrUpdateChatSession = async (sessionData: {
  userId: string;
  displayName: string;
  email?: string;
  lastActivity: any;
  status: 'active' | 'inactive' | 'closed';
}) => {
  const chatRef = doc(db, "liveChats", sessionData.userId);
  return await setDoc(chatRef, {
    ...sessionData,
    updatedAt: Timestamp.now()
  }, { merge: true });
};

// Customer services
export const createCustomer = async (customerData: Omit<Customer, 'id' | 'createdAt' | 'updatedAt'>) => {
  try {
    const customersCollection = collection(db, "customers");
    
    // Génère un ID unique pour le client (format: CUST-YYYYMMDD-XXXX)
    const date = new Date();
    const dateStr = date.getFullYear().toString() +
                   (date.getMonth() + 1).toString().padStart(2, '0') +
                   date.getDate().toString().padStart(2, '0');
    const randomPart = Math.floor(1000 + Math.random() * 9000); // 4 chiffres aléatoires
    const customerId = `CUST-${dateStr}-${randomPart}`;
    
    // Ajouter les champs manquants
    const completeCustomerData = {
      ...customerData,
      customerId,
      userIds: [], // Initialiser un tableau vide pour les utilisateurs associés
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };
    
    const docRef = await addDoc(customersCollection, completeCustomerData);
    return { id: docRef.id, ...completeCustomerData };
  } catch (error) {
    console.error("Error creating customer:", error);
    throw error;
  }
};

export const getCustomer = async (customerId: string): Promise<Customer | null> => {
  try {
    const customerRef = doc(db, "customers", customerId);
    const customerSnap = await getDoc(customerRef);
    
    if (customerSnap.exists()) {
      return { id: customerSnap.id, ...customerSnap.data() } as Customer;
    }
    return null;
  } catch (error) {
    console.error("Error getting customer:", error);
    return null;
  }
};

export const getCustomers = async (filters: { status?: 'active' | 'inactive' } = {}) => {
  try {
    const customersCollection = collection(db, "customers");
    let customersQuery = query(customersCollection, orderBy("createdAt", "desc"));
    
    if (filters.status) {
      customersQuery = query(customersQuery, where("status", "==", filters.status));
    }
    
    const querySnapshot = await getDocs(customersQuery);
    const customers: Customer[] = [];
    
    querySnapshot.forEach((doc) => {
      customers.push({
        id: doc.id,
        ...doc.data()
      } as Customer);
    });
    
    return customers;
  } catch (error) {
    console.error("Error getting customers:", error);
    return [];
  }
};

export const updateCustomer = async (customerId: string, customerData: Partial<Customer>) => {
  try {
    const customerRef = doc(db, "customers", customerId);
    await updateDoc(customerRef, {
      ...customerData,
      updatedAt: Timestamp.now()
    });
    return true;
  } catch (error) {
    console.error("Error updating customer:", error);
    return false;
  }
};

/**
 * Gets all users associated with a specific customer
 * @param customerId ID of the customer
 * @returns Promise with array of user profiles
 */
export const getUsersByCustomer = async (customerId: string): Promise<UserProfile[]> => {
  try {
    if (!customerId) {
      console.error("Invalid customerId provided");
      return [];
    }
    
    // D'abord, vérifier si le client a un champ userIds
    const customerRef = doc(db, "customers", customerId);
    const customerDoc = await getDoc(customerRef);
    
    if (customerDoc.exists()) {
      const customerData = customerDoc.data();
      const userIds = customerData.userIds || [];
      
      if (userIds.length > 0) {
        // Si nous avons des userIds, récupérer ces utilisateurs spécifiques
        const users: UserProfile[] = [];
        
        // Récupérer les utilisateurs par lots pour éviter trop de requêtes individuelles
        const userPromises = userIds.map(async (userId: string) => {
          const userProfile = await getUserProfile(userId);
          if (userProfile) {
            users.push(userProfile);
          }
        });
        
        await Promise.all(userPromises);
        console.log(`Found ${users.length} users for customer ${customerId} using userIds`);
        return users;
      }
    }
    
    // Méthode de secours: rechercher par customerId dans la collection users
    const usersCollection = collection(db, "users");
    const q = query(usersCollection, where("customerId", "==", customerId));
    const querySnapshot = await getDocs(q);
    
    const users: UserProfile[] = [];
    querySnapshot.forEach((doc) => {
      const userData = doc.data();
      users.push({
        id: doc.id,
        email: userData.email,
        displayName: userData.displayName,
        roleId: userData.roleId,
        customerId: userData.customerId,
        createdAt: userData.createdAt,
        updatedAt: userData.updatedAt
      } as UserProfile);
    });
    
    console.log(`Found ${users.length} users for customer ${customerId} using query`);
    return users;
  } catch (error) {
    console.error("Error getting users by customer:", error);
    return [];
  }
};

/**
 * Marks a ticket as read by admin
 * @param ticketId ID of the ticket to mark as read
 * @returns Promise with update result
 */
export const markTicketAsRead = async (ticketId: string) => {
  try {
    const ticketRef = doc(db, "tickets", ticketId);
    await updateDoc(ticketRef, {
      isRead: true,
      updatedAt: Timestamp.now()
    });
    return true;
  } catch (error) {
    console.error("Error marking ticket as read:", error);
    return false;
  }
};

/**
 * Links a user to a customer
 * @param userId ID of the user to link
 * @param customerId ID of the customer to link the user to
 * @returns Promise with update result
 */
export const linkUserToCustomer = async (userId: string, customerId: string): Promise<boolean> => {
  try {
    // Vérifier que l'utilisateur et le client existent
    const userRef = doc(db, "users", userId);
    const customerRef = doc(db, "customers", customerId);
    
    const [userDoc, customerDoc] = await Promise.all([
      getDoc(userRef),
      getDoc(customerRef)
    ]);
    
    if (!userDoc.exists()) {
      console.error("User document doesn't exist:", userId);
      return false;
    }
    
    if (!customerDoc.exists()) {
      console.error("Customer document doesn't exist:", customerId);
      return false;
    }
    
    // Mettre à jour l'utilisateur avec l'ID du client
    await updateDoc(userRef, {
      customerId,
      updatedAt: Timestamp.now()
    });
    
    // Mettre à jour le client avec l'ID de l'utilisateur
    const customerData = customerDoc.data();
    const userIds = customerData.userIds || [];
    
    // Ajouter l'utilisateur à la liste des utilisateurs associés au client s'il n'y est pas déjà
    if (!userIds.includes(userId)) {
      userIds.push(userId);
      await updateDoc(customerRef, {
        userIds,
        updatedAt: Timestamp.now()
      });
    }
    
    console.log(`User ${userId} linked to customer ${customerId} successfully`);
    
    return true;
  } catch (error) {
    console.error("Error linking user to customer:", error);
    return false;
  }
};

/**
 * Unlinks a user from their associated customer
 * @param userId ID of the user to unlink
 * @returns Promise with update result
 */
export const unlinkUserFromCustomer = async (userId: string): Promise<boolean> => {
  try {
    const userRef = doc(db, "users", userId);
    
    // Vérifier que l'utilisateur existe et récupérer son customerId actuel
    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) {
      console.error("User document doesn't exist:", userId);
      return false;
    }
    
    const userData = userDoc.data();
    const previousCustomerId = userData.customerId;
    
    if (previousCustomerId) {
      // Mettre à jour le client pour retirer l'utilisateur de sa liste
      const customerRef = doc(db, "customers", previousCustomerId);
      const customerDoc = await getDoc(customerRef);
      
      if (customerDoc.exists()) {
        const customerData = customerDoc.data();
        const userIds = customerData.userIds || [];
        
        // Filtrer pour retirer l'ID de l'utilisateur
        const updatedUserIds = userIds.filter((id: string) => id !== userId);
        
        await updateDoc(customerRef, {
          userIds: updatedUserIds,
          updatedAt: Timestamp.now()
        });
      }
    }
    
    // Mettre à jour l'utilisateur pour supprimer l'association avec le client
    await updateDoc(userRef, {
      customerId: null,
      updatedAt: Timestamp.now()
    });
    
    console.log(`User ${userId} unlinked from customer ${previousCustomerId} successfully`);
    
    return true;
  } catch (error) {
    console.error("Error unlinking user from customer:", error);
    return false;
  }
};

// Ajouter cette nouvelle fonction pour récupérer les tickets avec les informations utilisateur
export const getTicketsWithUserDetails = async (filters = {}) => {
  try {
    // Récupérer d'abord tous les tickets
    const ticketsCollection = collection(db, "tickets");
    let ticketsQuery = query(ticketsCollection, orderBy("createdAt", "desc"));
    
    // Appliquer les filtres si nécessaire
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        ticketsQuery = query(ticketsQuery, where(key, "==", value));
      }
    });
    
    const querySnapshot = await getDocs(ticketsQuery);
    const ticketsPromises = querySnapshot.docs.map(async (docSnapshot) => {
      const ticketData = { id: docSnapshot.id, ...docSnapshot.data() } as any;
      
      // Si le ticket a un userId, récupérer les informations de l'utilisateur
      if (ticketData.userId) {
        try {
          const userProfile = await getUserProfile(ticketData.userId);
          if (userProfile) {
            // Enrichir le ticket avec les informations de l'utilisateur
            ticketData.userName = userProfile.displayName || ticketData.userName;
            ticketData.userEmail = userProfile.email || ticketData.userEmail;
            
            // Si le ticket n'a pas de customerId mais que l'utilisateur est associé à un client
            if (!ticketData.customerId && userProfile.customerId) {
              try {
                const customerData = await getCustomer(userProfile.customerId);
                if (customerData) {
                  // Enrichir le ticket avec les informations du client associé à l'utilisateur
                  ticketData.customerName = customerData.companyName || customerData.name || 'Client sans nom';
                  // On peut aussi sauvegarder cette relation pour les prochaines requêtes
                  const ticketRef = doc(db, "tickets", ticketData.id);
                  updateDoc(ticketRef, {
                    customerId: userProfile.customerId,
                    updatedAt: Timestamp.now()
                  }).catch(err => console.warn("Couldn't update ticket with customerId:", err));
                }
              } catch (error) {
                console.error(`Error fetching customer from user for ticket ${ticketData.id}:`, error);
              }
            }
          }
        } catch (error) {
          console.error(`Error fetching user details for ticket ${ticketData.id}:`, error);
        }
      }
      
      // Si le ticket a un customerId, récupérer les informations du client
      if (ticketData.customerId) {
        try {
          const customerData = await getCustomer(ticketData.customerId);
          if (customerData) {
            // Enrichir le ticket avec les informations du client
            ticketData.customerName = customerData.companyName || customerData.name || 'Client sans nom';
          }
        } catch (error) {
          console.error(`Error fetching customer details for ticket ${ticketData.id}:`, error);
          // Définir une valeur par défaut pour customerName en cas d'erreur
          ticketData.customerName = ticketData.customerName || 'Client non identifié';
        }
      } else {
        // Si pas de customerId, afficher une valeur par défaut
        ticketData.customerName = 'Non associé';
      }
      
      return ticketData;
    });
    
    // Attendre que toutes les promesses soient résolues
    const tickets = await Promise.all(ticketsPromises);
    return tickets;
  } catch (error) {
    console.error("Error fetching tickets with user details:", error);
    return [];
  }
};

/**
 * Récupère tous les tickets associés à un client spécifique, y compris les tickets de tous ses utilisateurs
 * @param customerId ID du client ou customerId
 * @returns Promise avec les tickets enrichis
 */
export const getTicketsByCustomer = async (customerId: string) => {
  try {
    if (!customerId) {
      console.error("Invalid customerId provided");
      return [];
    }

    // Vérifier si l'ID fourni est un ID de document ou un customerId
    let customerDocId = customerId;
    let customerDoc;

    // D'abord, essayer de récupérer directement par l'ID du document
    const customerRef = doc(db, "customers", customerDocId);
    customerDoc = await getDoc(customerRef);
    
    // Si le document n'existe pas, essayer de chercher par customerId
    if (!customerDoc.exists()) {
      console.log(`Customer document not found with ID ${customerDocId}, trying to find by customerId`);
      
      const customersCollection = collection(db, "customers");
      const q = query(customersCollection, where("customerId", "==", customerId));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        console.error("Customer not found with ID or customerId:", customerId);
        return [];
      }
      
      // Utiliser le premier résultat trouvé
      customerDoc = querySnapshot.docs[0];
      customerDocId = customerDoc.id;
    }
    
    const customerData = customerDoc.data();
    const userIds = customerData.userIds || [];
    
    // Récupérer également les utilisateurs qui ont ce customerId (méthode de secours)
    const usersCollection = collection(db, "users");
    const usersQuery = query(usersCollection, where("customerId", "==", customerDocId));
    const usersSnapshot = await getDocs(usersQuery);
    
    const allUserIds = new Set<string>(userIds);
    usersSnapshot.forEach(doc => {
      allUserIds.add(doc.id);
    });
    
    // Si aucun utilisateur n'est associé, chercher directement les tickets par customerId
    if (allUserIds.size === 0) {
      const ticketsWithCustomerId = await getTicketsWithUserDetails({ customerId: customerDocId });
      return ticketsWithCustomerId;
    }
    
    // Récupérer tous les tickets pour tous les utilisateurs associés
    const userIdsArray = Array.from(allUserIds);
    
    // Récupérer les tickets pour chaque utilisateur
    const ticketsPromises = userIdsArray.map(userId => 
      getTicketsWithUserDetails({ userId })
    );
    
    // Ajouter aussi les tickets qui ont directement le customerId
    ticketsPromises.push(getTicketsWithUserDetails({ customerId: customerDocId }));
    
    // Attendre que toutes les requêtes soient terminées
    const ticketsArrays = await Promise.all(ticketsPromises);
    
    // Fusionner tous les tableaux de tickets
    const allTickets = ticketsArrays.flat();
    
    // Éliminer les doublons éventuels (par ID de ticket)
    const uniqueTickets = Array.from(
      new Map(allTickets.map(ticket => [ticket.id, ticket])).values()
    );
    
    console.log(`Found ${uniqueTickets.length} tickets for customer ${customerDocId} and its ${allUserIds.size} users`);
    return uniqueTickets;
  } catch (error) {
    console.error("Error getting tickets by customer:", error);
    return [];
  }
};

// Fonction pour mettre à jour le mot de passe d'un utilisateur
export const updateUserPassword = async (email: string, newPassword: string): Promise<boolean> => {
  try {
    // Firebase Auth ne permet pas de modifier directement le mot de passe d'un utilisateur
    // depuis le backend sans avoir son ancien mot de passe.
    // On doit utiliser l'API d'administration ou envoyer un email de réinitialisation

    // Envoyer un email de réinitialisation de mot de passe
    await sendPasswordResetEmail(auth, email);
    
    console.log(`Password reset email sent to ${email}`);
    return true;
  } catch (error) {
    console.error("Error sending password reset email:", error);
    return false;
  }
};

// Fonction pour bloquer un utilisateur
export const disableUser = async (userId: string): Promise<boolean> => {
  try {
    // Dans Firebase, on ne peut pas directement désactiver un compte depuis le client
    // Mais on peut marquer l'utilisateur comme désactivé dans Firestore
    const userRef = doc(db, "users", userId);
    
    await updateDoc(userRef, {
      isDisabled: true,
      updatedAt: Timestamp.now()
    });
    
    console.log(`User ${userId} has been disabled`);
    return true;
  } catch (error) {
    console.error("Error disabling user:", error);
    return false;
  }
};

// Fonction pour débloquer un utilisateur
export const enableUser = async (userId: string): Promise<boolean> => {
  try {
    const userRef = doc(db, "users", userId);
    
    await updateDoc(userRef, {
      isDisabled: false,
      updatedAt: Timestamp.now()
    });
    
    console.log(`User ${userId} has been enabled`);
    return true;
  } catch (error) {
    console.error("Error enabling user:", error);
    return false;
  }
};

/**
 * Gets all users from the database
 * @param limit Optional limit of users to retrieve
 * @returns Promise with array of user profiles
 */
export const getAllUsers = async (limitCount?: number): Promise<UserProfile[]> => {
  try {
    const usersCollection = collection(db, "users");
    let usersQuery;
    
    if (limitCount) {
      usersQuery = query(usersCollection, orderBy("createdAt", "desc"), limit(limitCount));
    } else {
      usersQuery = query(usersCollection, orderBy("createdAt", "desc"));
    }
    
    const querySnapshot = await getDocs(usersQuery);
    const users: UserProfile[] = [];
    
    querySnapshot.forEach((doc) => {
      const userData = doc.data();
      users.push({
        id: doc.id,
        email: userData.email,
        displayName: userData.displayName,
        roleId: userData.roleId,
        customerId: userData.customerId,
        createdAt: userData.createdAt,
        updatedAt: userData.updatedAt,
        lastLogin: userData.lastLogin,
        isDisabled: userData.isDisabled
      } as UserProfile);
    });
    
    return users;
  } catch (error) {
    console.error("Error getting all users:", error);
    return [];
  }
};

// Mise à jour de l'interface CustomerApp pour inclure les nouvelles propriétés
export interface CustomerApp {
  id: string;
  customerId: string;
  name: string;
  description?: string;
  status: 'active' | 'pending' | 'expired' | 'inactive' | 'development' | 'maintenance';
  lastUsed?: string;
  icon?: string;
  stage?: string;
  category?: string;
  techStack?: string;
  stacks?: string[]; // IDs des stacks technologiques
  urlApp?: string;
  link?: string;
  repository?: string;
  docsLink?: string;
  updates?: any[];
  createdAt?: any;
  updatedAt?: any;
  downloads?: {
    platform: 'ios' | 'android' | 'windows' | 'linux' | 'web';
    downloadUrl: string;
    version: string;
    status: 'active' | 'deprecated' | 'beta' | 'development';
    updatedAt?: any;
    history?: {
      version: string;
      downloadUrl: string;
      status: 'active' | 'deprecated' | 'beta' | 'development';
      releaseDate: any;
      notes?: string;
    }[];
  }[];
}

/**
 * Récupère les applications d'un client
 * @param customerId ID du client
 * @returns Promise avec un tableau d'applications
 */
export const getCustomerApps = async (customerId: string): Promise<CustomerApp[]> => {
  try {
    if (!customerId) {
      console.error("Invalid customerId provided");
      return [];
    }
    
    const appsCollection = collection(db, `customers/${customerId}/apps`);
    const appsQuery = query(appsCollection, orderBy("name"));
    const appsSnapshot = await getDocs(appsQuery);
    
    const apps: CustomerApp[] = [];
    appsSnapshot.forEach((doc) => {
      const appData = doc.data();
      apps.push({
        id: doc.id,
        ...appData,
        // Convertir les timestamps en dates JavaScript si nécessaire
        lastUsed: appData.lastUsed ? new Date(appData.lastUsed.seconds * 1000).toISOString() : new Date().toISOString(),
        // Assurer que le statut est dans un format attendu
        status: appData.status || 'active'
      } as CustomerApp);
    });
    
    return apps;
  } catch (error) {
    console.error("Error getting customer apps:", error);
    return [];
  }
};

/**
 * Interface pour les adresses d'un client avec la catégorie
 */
export interface CustomerAddress extends Address {
  category?: string;
}

/**
 * Récupère les adresses d'un client
 * @param customerId ID du client
 * @returns Promise avec un tableau d'adresses
 */
export const getCustomerAddresses = async (customerId: string): Promise<CustomerAddress[]> => {
  try {
    if (!customerId) {
      console.error("Invalid customerId provided");
      return [];
    }
    
    const addressesCollection = collection(db, `customers/${customerId}/addresses`);
    const addressesQuery = query(addressesCollection);
    const addressesSnapshot = await getDocs(addressesQuery);
    
    const addresses: CustomerAddress[] = [];
    addressesSnapshot.forEach((doc) => {
      const data = doc.data();
      addresses.push({
        id: doc.id,
        street: data.street || "",
        city: data.city || "",
        postalCode: data.postalCode || "",
        country: data.country || "",
        isMain: data.isMain || false,
        category: data.category || "",
        // Convertir les timestamps si nécessaire
        createdAt: data.createdAt ? new Date(data.createdAt.seconds * 1000) : null,
        updatedAt: data.updatedAt ? new Date(data.updatedAt.seconds * 1000) : null
      });
    });
    
    return addresses;
  } catch (error) {
    console.error("Error getting customer addresses:", error);
    return [];
  }
}; 